import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { api, FalhaDaApi } from '../api/cliente';
import { Alerta, Botao } from './base';
import { AreaDeTexto, Caixa, Campo, Seletor } from './campos';
import { Modal } from './Modal';
import { data as formatarData, moeda } from '../util/formato';

export interface ParcelaParaBaixar {
  readonly id: string;
  readonly numero: number;
  /** Em centavos, como toda saída de dinheiro da API. */
  readonly valor: number;
  readonly vencimento: string;
  /** Presente quando a parcela JÁ foi baixada — o diálogo passa a corrigir. */
  readonly pagamento?: {
    valorPago: number;
    dataPagamento: string;
    formaPagamento: string;
    observacoes?: string | null;
    /** O congelado na baixa, que pode não ser o do contrato da clínica. */
    percentualHonorarios?: number;
  } | null;
}

const FORMAS = [
  { valor: 'PIX', rotulo: 'PIX' },
  { valor: 'TRANSFERENCIA', rotulo: 'Transferência' },
  { valor: 'BOLETO', rotulo: 'Boleto' },
  { valor: 'CARTAO', rotulo: 'Cartão' },
  { valor: 'DINHEIRO', rotulo: 'Dinheiro' },
  { valor: 'OUTRO', rotulo: 'Outro' },
];

const ACEITOS = '.pdf,.jpg,.jpeg,.png,.webp,.heic';

/** Percentual como se escreve aqui: "20", "22,5". `null` quando não é número. */
function lerPercentual(texto: string): number | null {
  const limpo = texto.replace(/[%\s]/g, '').replace(',', '.');
  if (!limpo) return null;
  const n = Number(limpo);
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
}

/** 0 → "0", 22.5 → "22,5". Sem casas decimais penduradas. */
function comoPercentual(n: number): string {
  return String(n).replace('.', ',');
}

/** O que o campo oferece quando ela marca a caixa. É o combinado do escritório. */
const PERCENTUAL_SUGERIDO = '20';

/**
 * Como a caixa "percentual diferente" nasce ao abrir o diálogo.
 *
 * Baixa nova nasce no contrato da clínica — a exceção é a exceção, e marcá-la por
 * padrão faria toda parcela sair fora do contrato por distração.
 *
 * Corrigindo, o que manda é o percentual JÁ GRAVADO naquela baixa. Se ele não é o
 * do contrato, a caixa nasce marcada com ele: nascer desmarcada faria a correção
 * de um valor devolver a parcela ao percentual da clínica sem ninguém pedir.
 *
 * Exportada por ser a regra que decide dinheiro, e ser conferível sem abrir a tela.
 */
export function percentualInicial(
  doPagamento: number | undefined,
  daClinica: number,
): { proprio: boolean; texto: string } {
  const proprio = doPagamento !== undefined && doPagamento !== daClinica;
  return {
    proprio,
    texto: proprio ? comoPercentual(doPagamento) : PERCENTUAL_SUGERIDO,
  };
}

/** Centavos → "125,00", que é como se digita. Sem "R$": o rótulo já diz. */
function emReais(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Lê "130,00", "130.00" ou "1.130,00" em centavos, só para o aviso da tela. */
function lerCentavos(texto: string): number | null {
  const limpo = texto.replace(/[R$\s]/g, '');
  if (!limpo) return null;
  const normalizado = limpo.includes(',')
    ? limpo.replace(/\./g, '').replace(',', '.')
    : limpo;
  const n = Number(normalizado);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/**
 * Baixa de uma parcela.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUE O VALOR É EDITÁVEL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * O devedor paga direto na conta da clínica, e o que ele paga nem sempre é o que
 * estava combinado — arredonda para cima, paga um pedaço, junta com o mês anterior.
 * Registrar sempre o valor da parcela faria a prestação de contas mentir sobre
 * quanto entrou, e o honorário do escritório é calculado sobre o que ENTROU.
 *
 * A API sempre aceitou valor diferente; era a tela que mandava o corpo vazio e
 * baixava o valor exato, com data de hoje e PIX, sem perguntar nada.
 *
 * A baixa NÃO movimenta dinheiro: ela registra o que já aconteceu.
 */
export function ModalDarBaixa({
  aberto,
  aoFechar,
  parcela,
  devedorId,
  nomeDoDevedor,
  percentualHonorarios,
}: {
  aberto: boolean;
  aoFechar: () => void;
  parcela: ParcelaParaBaixar | null;
  devedorId: string;
  nomeDoDevedor: string;
  /**
   * O percentual do CONTRATO da clínica. É o padrão da baixa e o que a tela mostra
   * quando ninguém mexe; quem calcula de verdade é o servidor.
   */
  percentualHonorarios: number;
}) {
  /* Por instância, não constante de módulo: dois diálogos iguais montados na mesma
     tela dividiriam o id, e o `form=` do botão do rodapé resolve pelo primeiro. */
  const ID_DO_FORMULARIO = useId();

  const clienteConsulta = useQueryClient();
  const inputArquivo = useRef<HTMLInputElement>(null);

  const [valorPago, setValorPago] = useState('');
  const [dataPagamento, setDataPagamento] = useState('');
  const [formaPagamento, setFormaPagamento] = useState('PIX');
  const [observacoes, setObservacoes] = useState('');
  const [comprovante, setComprovante] = useState<{ nome: string; base64: string } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  /* Desmarcado, vale o contrato da clínica. Marcado, vale o que estiver no campo —
     e só nesta baixa. Ver o bloco "Percentual fora do contrato" mais abaixo. */
  const [percentualProprio, setPercentualProprio] = useState(false);
  const [percentualDigitado, setPercentualDigitado] = useState('20');

  /* Baixando, nasce com o valor combinado e a data de hoje — o caso comum é pagar
     certo, e quem paga diferente altera um campo só. Corrigindo, nasce com o que
     ESTÁ registrado: redigitar data e forma convida a perder alguma coisa. */
  useEffect(() => {
    const pg = parcela?.pagamento;
    setValorPago(pg ? emReais(pg.valorPago) : parcela ? emReais(parcela.valor) : '');
    setDataPagamento(
      pg ? pg.dataPagamento.slice(0, 10) : new Date().toISOString().slice(0, 10),
    );
    setFormaPagamento(pg?.formaPagamento ?? 'PIX');
    setObservacoes(pg?.observacoes ?? '');
    const inicial = percentualInicial(pg?.percentualHonorarios, percentualHonorarios);
    setPercentualProprio(inicial.proprio);
    setPercentualDigitado(inicial.texto);
    setComprovante(null);
    setErro(null);
    if (inputArquivo.current) inputArquivo.current.value = '';
  }, [parcela, aberto, percentualHonorarios]);

  const centavosDigitados = lerCentavos(valorPago);
  const diferenca =
    parcela && centavosDigitados !== null ? centavosDigitados - parcela.valor : 0;

  const corrigindo = parcela?.pagamento != null;

  const percentualEscrito = lerPercentual(percentualDigitado);
  /* O que vale nesta baixa. Campo vazio ou fora de 0–100 cai no da clínica em vez
     de virar `NaN` na conta — a recusa de verdade vem do `submeter`. */
  const percentualEfetivo = percentualProprio
    ? (percentualEscrito ?? percentualHonorarios)
    : percentualHonorarios;

  const baixar = useMutation({
    mutationFn: () =>
      (corrigindo ? api.substituir : api.criar)(`/parcelas/${parcela?.id}/baixa`, {
        valorPago,
        dataPagamento,
        formaPagamento,
        /* Vai SEMPRE, e não só quando a caixa está marcada: corrigindo, campo
           ausente preserva o percentual já gravado, e desmarcar a caixa não
           desfaria nada. Mandando o efetivo, desmarcar volta ao da clínica. */
        percentualHonorarios: comoPercentual(percentualEfetivo),
        ...(observacoes.trim() ? { observacoes: observacoes.trim() } : {}),
        ...(comprovante
          ? { comprovanteBase64: comprovante.base64, comprovanteNome: comprovante.nome }
          : {}),
      }),
    onSuccess: () => {
      void clienteConsulta.invalidateQueries({ queryKey: ['devedor', devedorId] });
      void clienteConsulta.invalidateQueries({ queryKey: ['devedores'] });
      void clienteConsulta.invalidateQueries({ queryKey: ['painel'] });
      void clienteConsulta.invalidateQueries({ queryKey: ['pagamentos'] });
      void clienteConsulta.invalidateQueries({ queryKey: ['contagens'] });
      aoFechar();
    },
    onError: (e) =>
      setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível registrar a baixa.'),
  });

  function lerArquivo(evento: ChangeEvent<HTMLInputElement>) {
    const escolhido = evento.target.files?.[0];
    if (!escolhido) {
      setComprovante(null);
      return;
    }
    setErro(null);
    const leitor = new FileReader();
    leitor.onload = () => setComprovante({ nome: escolhido.name, base64: String(leitor.result) });
    leitor.onerror = () => setErro('Não foi possível ler o arquivo.');
    leitor.readAsDataURL(escolhido);
  }

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    if (centavosDigitados === null || centavosDigitados <= 0) {
      setErro('Informe o valor que o devedor pagou.');
      return;
    }
    if (percentualProprio && percentualEscrito === null) {
      setErro('Informe o percentual desta parcela, de 0 a 100 — ou desmarque a caixa.');
      return;
    }
    baixar.mutate();
  }

  if (!parcela) return null;

  const honorarios = Math.round(((centavosDigitados ?? 0) * percentualEfetivo) / 100);

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={
        corrigindo
          ? `Corrigir o pagamento da parcela ${parcela.numero}`
          : parcela.numero === 0
            ? 'Baixar a entrada'
            : `Baixar a parcela ${parcela.numero}`
      }
      descricao={`${nomeDoDevedor} · vencimento ${formatarData(parcela.vencimento)}`}
      largura="32rem"
      rodape={
        <>
          <Botao onClick={aoFechar} desabilitado={baixar.isPending}>
            Cancelar
          </Botao>
          <Botao
            tipo="primario"
            submeter
            formulario={ID_DO_FORMULARIO}
            desabilitado={baixar.isPending}
          >
            {baixar.isPending
              ? 'Salvando…'
              : corrigindo
                ? 'Salvar correção'
                : 'Registrar baixa'}
          </Botao>
        </>
      }
    >
      <form id={ID_DO_FORMULARIO} onSubmit={submeter} className="space-y-4">
        {erro && <Alerta tom="vencido">{erro}</Alerta>}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo
            rotulo="Valor pago (R$)"
            valor={valorPago}
            aoMudar={(v) => {
              setValorPago(v);
              setErro(null);
            }}
            placeholder="130,00"
            auxilio={`Combinado: ${moeda(parcela.valor)}`}
          />
          <Campo
            rotulo="Data do pagamento"
            tipo="date"
            valor={dataPagamento}
            aoMudar={setDataPagamento}
            auxilio="A data do comprovante, não a de hoje."
          />
        </div>

        {/* A diferença aparece assim que ela é digitada. Sem isto, um zero a mais
            passa despercebido e vira honorário errado na prestação de contas. */}
        {diferenca !== 0 && centavosDigitados !== null && (
          <Alerta tom={diferenca > 0 ? 'recebido' : 'atencao'}>
            {diferenca > 0
              ? `Pagou ${moeda(diferenca)} A MAIS que o combinado.`
              : `Pagou ${moeda(-diferenca)} A MENOS que o combinado.`}{' '}
            A parcela fica quitada com {moeda(centavosDigitados)}
            {diferenca < 0 && ' — a diferença não vira saldo a cobrar'}.
          </Alerta>
        )}

        <Seletor
          rotulo="Forma de pagamento"
          valor={formaPagamento}
          aoMudar={setFormaPagamento}
          opcoes={FORMAS}
        />

        <div>
          <span className="mb-1 block text-xs font-medium text-[color:var(--texto-suave)]">
            Comprovante (opcional)
          </span>
          <input
            ref={inputArquivo}
            type="file"
            accept={ACEITOS}
            onChange={lerArquivo}
            className="block w-full text-sm text-[color:var(--texto)] file:mr-3 file:rounded-md file:border file:bg-[color:var(--superficie)] file:px-3 file:py-1.5 file:text-sm file:text-[color:var(--texto)]"
          />
          {comprovante && (
            <p className="mt-1 text-xs text-[color:var(--texto-tenue)]">{comprovante.nome}</p>
          )}
        </div>

        <AreaDeTexto
          rotulo="Observações (opcional)"
          valor={observacoes}
          aoMudar={setObservacoes}
          linhas={2}
          placeholder="Ex.: pagou junto com o mês anterior."
        />

        {/* ═══════════════════════════════════════════════════════════════════
            PERCENTUAL FORA DO CONTRATO, SÓ NESTA PARCELA
            ═══════════════════════════════════════════════════════════════════

            Há clínica contratada sem êxito — 0% — em que uma parcela específica
            rende honorário assim mesmo: a que atrasou e precisou de negociação.
            O contrato continua a 0%; o que muda é esta baixa.

            Fica desmarcado por padrão, porque a exceção é a exceção. Marcar não
            toca no cadastro da clínica nem em nenhuma outra parcela. */}
        <fieldset className="space-y-3 border-t pt-4">
          <Caixa
            rotulo="Cobrar um percentual diferente nesta parcela"
            marcado={percentualProprio}
            aoMudar={(v) => {
              setPercentualProprio(v);
              setErro(null);
            }}
          />
          {percentualProprio ? (
            <div className="sm:w-48">
              <Campo
                rotulo="Percentual desta parcela (%)"
                valor={percentualDigitado}
                aoMudar={(v) => {
                  setPercentualDigitado(v);
                  setErro(null);
                }}
                placeholder={PERCENTUAL_SUGERIDO}
                auxilio={`O contrato da clínica é ${comoPercentual(percentualHonorarios)}%.`}
                {...(percentualEscrito === null && percentualDigitado.trim() !== ''
                  ? { erro: 'De 0 a 100.' }
                  : {})}
              />
            </div>
          ) : (
            <p className="text-xs text-[color:var(--texto-tenue)]">
              Sem marcar, vale o contrato da clínica:{' '}
              {comoPercentual(percentualHonorarios)}% sobre o que entrou.
            </p>
          )}
        </fieldset>

        {/* A conta, à vista, antes de confirmar. O percentual é congelado na baixa:
            mudar o contrato da clínica depois não mexe no que já foi apurado. */}
        {centavosDigitados !== null && centavosDigitados > 0 && (
          <dl className="rounded-md border bg-[color:var(--superficie-afundada)] p-3 text-xs">
            <div className="flex justify-between">
              <dt className="text-[color:var(--texto-suave)]">
                Honorários do escritório ({comoPercentual(percentualEfetivo)}%)
                {percentualEfetivo !== percentualHonorarios && (
                  <span className="ml-1 text-[color:var(--texto-tenue)]">
                    — só nesta parcela
                  </span>
                )}
              </dt>
              <dd className="tabular font-medium" style={{ color: 'var(--acento)' }}>
                {moeda(honorarios)}
              </dd>
            </div>
            <div className="mt-1 flex justify-between">
              <dt className="text-[color:var(--texto-suave)]">Permanece com a clínica</dt>
              <dd className="tabular">{moeda(centavosDigitados - honorarios)}</dd>
            </div>
          </dl>
        )}

        <p className="text-xs text-[color:var(--texto-tenue)]">
          {corrigindo
            ? 'A baixa antiga é desfeita e a nova é registrada na mesma operação, ' +
              'com o percentual que estiver acima.'
            : 'O dinheiro entrou direto na conta da clínica — esta baixa apenas ' +
              'registra o que já aconteceu.'}
        </p>
      </form>
    </Modal>
  );
}
